import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDirtyFieldValidation, isValid } from '@totalsoft/pure-validations-react';
import { financialProductValidator } from '../../../validators';
import { useToast } from 'hooks/toasts/useToast';
import { ADD_UPDATE_FINANCIAL_PRODUCT } from '../../mutations';
import { FINANCIAL_PRODUCT_QUERY, LEASING_PROMOTIONS_QUERY, DOCUMENT_LEASING_INCOME_SCHEMAS_QUERY } from '../../queries';
import { CURRENCIES_DICTIONARY_QUERY, SCHEDULE_TYPES_QUERY, LEASING_INTEREST_TYPES_QUERY } from 'features/common/queries';
import { CONTRACT_TYPES_DICTIONARY_QUERY } from '../../../list/queries';
import { FINANCIAL_PRODUCT_UPDATED } from '../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { emptyArray } from 'utils/constants';
import { omit } from 'ramda';
import { Prompt } from 'react-router-dom';
import Assignment from '@mui/icons-material/Assignment';
import FinancialProductGeneralDataComponent from './FinancialProductGeneralDataComponent';
import { Card, IconButton } from '@totalsoft/rocket-ui';
import { isDirty } from '@totalsoft/change-tracking';

const defaultFinancialProductState = {
  leasingFinancialProduct: {
    validFrom: null,
    validTo: null
  }
};

const FinancialProductGeneralDataContainer = ({ canWrite, productId, onRedirect, onChangeTitle, setGeneralDataLens }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultFinancialProductState);
  const [saving, setSaving] = useState(false);
  setGeneralDataLens(lens);

  const [financialProductValidation, validateFinancialProduct] = useDirtyFieldValidation(financialProductValidator);

  useQuery(FINANCIAL_PRODUCT_QUERY, {
    variables: { productId },
    onCompleted: data => {
      resetLens(omit(['financialProductXBusinessFlow', 'financialProductRules', 'financialProductFilters'], data.financialProduct));
      if (data.financialProduct?.productName) {
        onChangeTitle(data.financialProduct.productName);
      }
    },
    skip: !productId
  });

  const { data: contractTypesDictionaryData } = useQuery(CONTRACT_TYPES_DICTIONARY_QUERY);

  const { data: leasingPromotionsData } = useQuery(LEASING_PROMOTIONS_QUERY);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: scheduleTypesData } = useQuery(SCHEDULE_TYPES_QUERY);

  const { data: leasingIncomeSchemasData } = useQuery(DOCUMENT_LEASING_INCOME_SCHEMAS_QUERY, {
    variables: { documentId: productId ?? null }
  });

  const { data: leasingInterestTypesData } = useQuery(LEASING_INTEREST_TYPES_QUERY, {
    variables: { currencyId: lens.leasingFinancialProduct.creditCurrencyId |> get }
  });

  const [addOrUpdateFinancialProduct] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT, {
    onError: () => {
      setSaving(false);
    }
  });

  useSubscription(FINANCIAL_PRODUCT_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.FinancialProductUpdated'), 'success');
      resetLens();
      if (productId === undefined) onRedirect(data.financialProductUpdated.financialProduct.productId);
      setSaving(false);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialProducts' });
      client.cache.gc();
    }
  });

  const handleSave = () => {
    const product = lens |> get;
    if (!validateFinancialProduct(product)) return;
    setSaving(true);
    addOrUpdateFinancialProduct({
      variables: {
        input: {
          productId: parseInt(product.productId),
          productName: product.productName,
          productCode: product.productCode,
          description: product.description,
          isActive: product.isActive,
          leasingFinancialProduct: { ...product.leasingFinancialProduct }
        }
      }
    });
  };

  useEffect(() => {
    validateFinancialProduct(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProduct]);

  return (
    <Card
      icon={Assignment}
      iconColor='primary'
      title={t('FinancialProduct.Header')}
      actions={
        <IconButton
          type='save'
          fontSize='medium'
          color='transparent'
          tooltip={t('SaveChanges')}
          onClick={handleSave}
          disabled={!isDirty(dirtyInfo) || saving || !canWrite || !isValid(financialProductValidation)}
        />
      }
    >
      <Prompt when={isDirty(dirtyInfo) && !saving} message={t('FinancialProduct.LeavingWithoutSavingFinancialProduct')} />
      <FinancialProductGeneralDataComponent
        canWrite={canWrite}
        financialProduct={lens}
        contractTypes={contractTypesDictionaryData?.contractTypesDictionary ?? emptyArray}
        leasingPromotions={leasingPromotionsData?.leasingPromotions ?? emptyArray}
        currencies={currenciesDictionaryData?.currenciesDictionary ?? emptyArray}
        scheduleTypes={scheduleTypesData?.scheduleTypes ?? emptyArray}
        leasingIncomeSchemas={leasingIncomeSchemasData?.documentLeasingIncomeSchemas ?? emptyArray}
        leasingInterestTypes={leasingInterestTypesData?.leasingInterestTypes ?? emptyArray}
        validation={financialProductValidation}
        productId={productId}
      />
    </Card>
  );
};

FinancialProductGeneralDataContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  productId: PropTypes.number,
  onRedirect: PropTypes.func,
  onChangeTitle: PropTypes.func,
  setGeneralDataLens: PropTypes.func.isRequired
};

export default FinancialProductGeneralDataContainer;
